/**
 * FEAT-018: 파일 미리보기/다운로드 공통 유틸리티
 */

// SVG 제외 — Stored XSS 위험 (OWASP A03:2021)
export const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
export const PDF_EXTS = ['pdf'];

export const getFileExt = (filename) =>
  ((filename || '').split('.').pop() || '').toLowerCase();

export const isImage = (filename) => IMAGE_EXTS.includes(getFileExt(filename));
export const isPdf = (filename) => PDF_EXTS.includes(getFileExt(filename));
export const isPreviewable = (filename) => isImage(filename) || isPdf(filename);

/** URL allowlist — javascript:/data: 스킴 차단 (OWASP A03:2021) */
const ALLOWED_HOSTS = new Set([
  'common.rayplatform.com',
  's3.ap-northeast-2.amazonaws.com',
]);

export const isAllowedUrl = (u) => {
  try {
    const p = new URL(u);
    return /^https?:$/.test(p.protocol) && ALLOWED_HOSTS.has(p.hostname);
  } catch {
    return false;
  }
};

/** R2 경로 검증 — path traversal 차단 */
const R2_PATH_RE = /^rayguard\/[A-Za-z0-9._\-/]+$/;

export const isValidR2Path = (p) =>
  typeof p === 'string' &&
  R2_PATH_RE.test(p) &&
  !p.includes('..') &&
  !p.includes('//');

/** 파일 URL 해결: directLink → http URL → R2 path → S3 signed URL */
export const resolveFileUrl = async (att, apiPost) => {
  if (att.directLink && isAllowedUrl(att.directLink)) return att.directLink;
  if (att.path && /^https?:\/\//i.test(att.path) && isAllowedUrl(att.path)) return att.path;
  if (att.path && isValidR2Path(att.path)) {
    return `https://common.rayplatform.com/file/getfile?filepath=${encodeURIComponent(att.path)}`;
  }
  // S3 fallback
  try {
    const ext = getFileExt(att.path || att.name);
    const body = isImage(att.name)
      ? { path: att.path, ctype: 'image/' + ext }
      : { path: att.path, name: att.name };
    const ret = await apiPost(body);
    if (ret.data.status === "success") return ret.data.data;
  } catch (e) {
    console.error('Failed to get file URL:', e?.response?.status || e?.message);
  }
  return '';
};

/** 파일명 sanitize — path traversal, 제어문자, 이중 확장자 방어 */
export const sanitizeFilename = (n) =>
  (n || 'download')
    .replace(/[\r\n\t\x00-\x1f/\\:*?"<>|]+/g, '_')
    .replace(/\.\.+/g, '_')
    .slice(0, 200);

/** 원본 파일명으로 다운로드 (fetch+blob), CORS 실패 시 새 탭 폴백 */
export const downloadWithName = async (url, filename) => {
  const safeName = sanitizeFilename(filename);
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch (e) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};
